/** Validatie van antwoorden per wizard-stap.
 *
 * Wizard-indeling v3 (7 steps zichtbaar; AVG is een aparte phase).
 *   1. intake                  (7 verplichte + 1 optioneel)
 *   2. pijn                    (3 velden: pijn_voet + pijn_locaties + pijn_frequentie)
 *   3. bewegen                 (4 velden)
 *   4. vermoeidheid_werkvermogen (2 velden)
 *   5. werkfunctioneren        (5 velden)
 *   6. werkbeleving            (6 velden)
 *   7. klachten_elders         (1 verplicht multi-select + 1 optioneel vrij tekst)
 */
import { EMPTY_ANTWOORDEN } from "./antwoordenSchema";

function isLeeg(v) {
  if (v === "" || v === null || v === undefined) return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

export const STEP_GROUPS = {
  intake: [
    "leeftijd", "geslacht", "lengte_cm", "gewicht_kg",
    "functie_sector", "uur_staan_lopen", "type_werkschoen",
  ],
  pijn: ["pijn_voet", "pijn_locaties", "pijn_frequentie"],
  bewegen: ["bewegen_huishouden", "bewegen_traplopen", "bewegen_wandelen", "bewegen_boodschappen"],
  vermoeidheid_werkvermogen: ["vermoeidheid_einde_werkdag", "werkvermogen"],
  werkfunctioneren: [
    "werk_start", "werk_fouten", "werk_bewegingen", "werk_concentratie", "werk_multitasken",
  ],
  werkbeleving: [
    "werkbeleving_stress", "werkbeleving_taken", "werkbeleving_plezier",
    "werkbeleving_zelfvertrouwen", "werkbeleving_concentratie", "werkbeleving_energie",
  ],
  klachten_elders: ["klachten_elders"],
};

/** Index 1-7 → step-groep-key. */
export const STEP_KEY_BY_INDEX = {
  1: "intake",
  2: "pijn",
  3: "bewegen",
  4: "vermoeidheid_werkvermogen",
  5: "werkfunctioneren",
  6: "werkbeleving",
  7: "klachten_elders",
};

/** Geeft array van missende veldnamen voor de gegeven stap. */
export function validateStep(stepKey, antwoorden) {
  const fields = STEP_GROUPS[stepKey] || [];
  return fields.filter((f) => isLeeg(antwoorden?.[f]));
}

// ── Bereik-/typecontrole per veld (v4/data-gestuurd) ─────────────────────────
// Spiegelt de backend-validator (backend/app/services/antwoord_validator.py):
// een getal moet geldig zijn én binnen min/max vallen, schaalwaarden binnen
// bereik. Zo vangen we de 422 al vóór Versturen af — met een veld-specifieke,
// begrijpelijke melding i.p.v. de kale generieke tekst.

const SCALE_TYPES = new Set(["scale_0_10", "scale_1_5", "agree_1_5"]);

/**
 * Normaliseert NL-invoer naar een getal (komma → punt, zoals de backend en
 * `toBackendPayload` doen). Retourneert `null` als het geen geldig getal is.
 */
export function parseGetal(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v === null || v === undefined) return null;
  const s = String(v).trim().replace(",", ".");
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function eenheidSuffix(veld) {
  return veld?.eenheid ? ` ${veld.eenheid}` : "";
}

/**
 * Controleert één ingevulde veldwaarde tegen zijn schema-definitie.
 * Retourneert `null` als geldig (óf leeg — leegte is de leeg-check zijn taak),
 * anders een begrijpelijke NL-foutmelding die het veld benoemt.
 */
export function valideerVeldWaarde(veldId, veld, waarde) {
  if (!veld) return null;
  if (isLeeg(waarde)) return null; // leeg → afgehandeld door de leeg-check

  const type = veld.type;
  const vraag = veld.vraag || veldId;

  if (type === "number") {
    const num = parseGetal(waarde);
    if (num === null) {
      return `Vul een geldig getal in bij "${vraag}".`;
    }
    const lo = veld.min;
    const hi = veld.max;
    const buitenBereik = (lo != null && num < lo) || (hi != null && num > hi);
    // Klassieke eenheid-fout (lengte in meters "1,83" i.p.v. cm "183",
    // gewicht met verkeerde eenheid): geef een gerichte hint i.p.v. droog min/max.
    if (buitenBereik && veldId === "lengte_cm") {
      return "Vul uw lengte in hele centimeters in, bijvoorbeeld 183.";
    }
    if (buitenBereik && veldId === "gewicht_kg") {
      return "Vul uw gewicht in hele kilo's in, bijvoorbeeld 78.";
    }
    if (lo != null && num < lo) {
      return `Vul bij "${vraag}" een waarde van minimaal ${lo}${eenheidSuffix(veld)} in.`;
    }
    if (hi != null && num > hi) {
      return `Vul bij "${vraag}" een waarde van maximaal ${hi}${eenheidSuffix(veld)} in.`;
    }
    return null;
  }

  if (SCALE_TYPES.has(type)) {
    const num = parseGetal(waarde);
    const lo = veld.min ?? (type === "scale_0_10" ? 0 : 1);
    const hi = veld.max ?? (type === "scale_0_10" ? 10 : 5);
    if (num === null || num < lo || num > hi) {
      return `Kies bij "${vraag}" een waarde tussen ${lo} en ${hi}.`;
    }
    return null;
  }

  return null;
}

/**
 * Bereik-/typecontrole voor een lijst veld-id's (de zichtbare velden van een
 * stap). Retourneert `[{ veldId, message }]` voor élk veld met een ingevulde,
 * maar ongeldige waarde. Lege verplichte velden vallen hier NIET onder (dat is
 * de leeg-check); optionele lege velden ook niet.
 */
export function valideerBereik(veldIds, schema, antwoorden) {
  const veldenDef = schema?.velden || {};
  const fouten = [];
  (veldIds || []).forEach((id) => {
    const msg = valideerVeldWaarde(id, veldenDef[id], antwoorden?.[id]);
    if (msg) fouten.push({ veldId: id, message: msg });
  });
  return fouten;
}

// Velden die als komma-decimaal ingevoerd worden ("7,5" / "42,5") en dus een
// puntnormalisatie + parseFloat nodig hebben. Zonder schema (v3-fallback) is dit
// de hardcoded lijst; met schema wordt dit uit de veld-types afgeleid.
const DECIMAAL_VELDEN = ["uur_staan_lopen", "schoenmaat_eu"];

function normaliseerFloat(v) {
  // NL-invoer gebruikt een komma ("7,5"). parseFloat("7,5") geeft 7, dus eerst
  // naar een punt normaliseren; anders verliest de meting stil een halve waarde.
  return parseFloat(String(v).replace(",", "."));
}

/**
 * Casteert antwoorden naar het juiste type voor de backend.
 *
 * @param {object} antwoorden  huidige antwoord-state
 * @param {object|null} schema  optioneel v4-`vragen_json`. Als meegegeven en met
 *   `velden`, wordt datagedreven gecast op basis van veld-`type` i.p.v. de
 *   hardcoded key-lijsten. Zonder schema → ongewijzigd v3-gedrag.
 */
export function toBackendPayload(antwoorden, schema = null) {
  if (schema && schema.velden) {
    return toBackendPayloadDatagedreven(antwoorden, schema);
  }

  const out = { ...EMPTY_ANTWOORDEN, ...antwoorden };
  // Int-velden
  [
    "leeftijd", "lengte_cm", "gewicht_kg",
    "pijn_voet",
    "bewegen_huishouden", "bewegen_traplopen", "bewegen_wandelen", "bewegen_boodschappen",
    "vermoeidheid_einde_werkdag",
    "werkvermogen",
    "werk_start", "werk_fouten", "werk_bewegingen", "werk_concentratie", "werk_multitasken",
    "werkbeleving_stress", "werkbeleving_taken", "werkbeleving_plezier",
    "werkbeleving_zelfvertrouwen", "werkbeleving_concentratie", "werkbeleving_energie",
  ].forEach((k) => {
    if (out[k] !== null && out[k] !== "") out[k] = parseInt(out[k], 10);
  });
  // Float — komma-normalisatie voor decimale velden (uur_staan_lopen; en
  // schoenmaat_eu zodra die in v4-antwoorden voorkomt).
  DECIMAAL_VELDEN.forEach((k) => {
    if (out[k] !== undefined && out[k] !== "" && out[k] !== null) {
      out[k] = normaliseerFloat(out[k]);
    }
  });
  // Optional string defaults
  if (out.voet_klachten_voorgeschiedenis === "") out.voet_klachten_voorgeschiedenis = "geen";
  // Klachten-elders vrije tekst alleen relevant als "anders" geselecteerd
  if (!Array.isArray(out.klachten_elders) || !out.klachten_elders.includes("anders")) {
    out.klachten_elders_anders = "";
  }
  return out;
}

/** Datagedreven cast op basis van veld-type uit het v4-schema. */
function toBackendPayloadDatagedreven(antwoorden, schema) {
  const out = { ...antwoorden };
  const velden = schema.velden || {};

  Object.entries(velden).forEach(([key, def]) => {
    const v = out[key];
    if (v === undefined || v === null || v === "") return;

    switch (def.type) {
      case "number": {
        // Decimale velden (halve maten, uren) via komma-normalisatie; de rest int.
        if (def.inputmode === "decimal") out[key] = normaliseerFloat(v);
        else out[key] = parseInt(v, 10);
        break;
      }
      case "scale_0_10":
      case "scale_1_5":
      case "agree_1_5": {
        out[key] = parseInt(v, 10);
        break;
      }
      // choice/text/date/email/tel/multi_select blijven ongewijzigd (string/array).
      default:
        break;
    }
  });

  // Conditionele tekstvelden legen als hun bronveld de trigger niet (meer) bevat.
  Object.entries(velden).forEach(([key, def]) => {
    const cond = def.toon_als;
    if (!cond || !cond.veld) return;
    const bron = out[cond.veld];
    const zichtbaar = Array.isArray(bron) ? bron.includes(cond.bevat) : bron === cond.bevat;
    if (!zichtbaar) out[key] = "";
  });

  return out;
}
